import React, { useState, useEffect } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import { useDispatch } from "react-redux";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import Button from "../../../component-lib/Molecules/button/Button";
import Input from "../../../component-lib/Atoms/Input/Input";
import { ADD_CHAT_GROUP_FIELDS } from "./constant";
import EventEHRMultiSelectWithSearch from "../../../component-lib/Atoms/Select/EventEHRMultiSelectWithSearch";
import {
  getLazyLoadingApiResponse,
  getLazyLoadingSearchApiResponse,
} from "../../../utils/constantsFunctions";
import {
  hideLoader,
  showLoader,
} from "../../../component-lib/Molecules/loader/LoaderSlice";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    BorderAllRounded: "8px",
    Height: "300px",
    width: "708px", // Set your custom width here
    maxWidth: "none", // Prevents Material-UI from overriding your width
  },
}));

interface GroupChatDialogDialogProps {
  isOpen: boolean;
  handleData: (data: any) => void;
  handleClose: () => void;
  defaultDataForUser?: any;
  isEdit?: boolean;
  dialogHeading?: string;
}

export default function GroupChatDialog({
  isOpen = false,
  defaultDataForUser,
  handleClose,
  handleData,
  isEdit = false,
  dialogHeading,
}: GroupChatDialogDialogProps) {
  const dispatch = useDispatch();
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);

  // Paginated (infinite-scroll) user list — mirrors the working Search Users
  // list in UsersList.tsx. Options accumulate locally as the user scrolls.
  const [pageCountForGetUser, setPageCountForUser] = useState(1);
  const [dropdownSearchForUser, setDropdownSearchForUser] = useState("");
  const [dropdownOptionsForUser, setDropdownOptionsForUser] = useState<any>([]);
  const [resetFlagForUser, setResetFlagForUser] = useState(false);

  const mapUsers = (usersList: any) =>
    usersList
      ?.map((data: any) => ({
        label:
          data?.firstName || data?.lastName
            ? `${data?.firstName + " " + data?.lastName}`
            : "-",
        value: data?.id,
      }));

  useEffect(() => {
    if (!resetFlagForUser) {
      if (dropdownSearchForUser && dropdownSearchForUser.length > 0) {
        getDropdownSearchListForUser(dropdownSearchForUser);
      } else {
        getDropdownListForUser(pageCountForGetUser);
      }
    }
  }, [dropdownSearchForUser, pageCountForGetUser]);

  useEffect(() => {
    if (resetFlagForUser) {
      resetListForUser();
    }
  }, [resetFlagForUser]);

  const resetListForUser = () => {
    setDropdownSearchForUser("");
    setPageCountForUser(1);
    getDropdownListForUser(1, true);
  };

  // Fetch a page of chat users and APPEND (dedupe) as the user scrolls.
  const getDropdownListForUser = async (pageCount: any, reset = false) => {
    dispatch(showLoader());
    try {
      const response = await getLazyLoadingApiResponse("user", pageCount, 8, {
        isForChat: true,
      });
      const mappedUserData = mapUsers(response?.data?.users || []);
      if (mappedUserData && mappedUserData.length > 0) {
        if (reset) {
          setDropdownOptionsForUser(mappedUserData);
          setResetFlagForUser(false);
        } else if (
          !dropdownOptionsForUser?.some(
            (option: any) => option?.value === mappedUserData[0]?.value
          )
        ) {
          setDropdownOptionsForUser(
            dropdownOptionsForUser.concat(mappedUserData)
          );
        }
      }
    } catch (error) {
      console.log("error:", error);
    } finally {
      dispatch(hideLoader());
    }
  };

  // Server-side search by name (replaces the list).
  const getDropdownSearchListForUser = async (search: string) => {
    try {
      const response = await getLazyLoadingSearchApiResponse(
        "user",
        search,
        "name",
        { isForChat: true }
      );
      setDropdownOptionsForUser(mapUsers(response?.data?.users || []) || []);
    } catch (error) {
      console.log("error:", error);
    }
  };

  const validationSchema = Yup.object().shape({
    [ADD_CHAT_GROUP_FIELDS?.GROUP_NAME]: Yup.string().required(
      "Group Name is required"
    ),
    [ADD_CHAT_GROUP_FIELDS?.USER_NAME]: Yup.array()
      .min(1, "At least one user must be selected")
      .required("This field is required"),
  });

  // IF SEARCH VALUE  THEN WE ARE GIVEN SOME TIMER
  const handleSearchedValueChange = (data: any) => {
    if (timer) {
      clearTimeout(timer); // Clear the previous timer
    }
    // Set a new timer with your desired delay (e.g., 500ms)
    const newTimer = setTimeout(() => {
      setDropdownSearchForUser(data);
    }, 500); // Adjust the delay as needed (in milliseconds)

    setTimer(newTimer); // Store the timer ID for cleanup
  };

  const getMemembers = defaultDataForUser?.memebers
    ?.map((data: any) => ({
      label:
        data?.users?.firstName || data?.users?.lastName
          ? `${data?.users?.firstName + " " + data?.users?.lastName}`
          : "-",
      values: data?.userId,
    }));

  return (
    <BootstrapDialog
      onClose={(event: any, reason: string) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [ADD_CHAT_GROUP_FIELDS?.GROUP_NAME]: `${defaultDataForUser?.name === undefined ? "" : defaultDataForUser?.name}`,
          [ADD_CHAT_GROUP_FIELDS?.USER_NAME]: getMemembers || [],
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            console.log(values);
            handleData(values);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false); // Reset submitting state
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }: any) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="customized-dialog-title"
              >
                {dialogHeading}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              {/* MAIN CONTENT */}
              <DialogContent>
                <div
                  style={{
                    // height: deviceType === "mobile" ? "40vh" : "fit-content",
                    overflow: "auto",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                  }}
                >
                  <div className="pt-4">
                    <Input
                      textFieldLabel="Group Name"
                      // placeholder=""
                      maxLength={20}
                      size="small"
                      name={ADD_CHAT_GROUP_FIELDS.GROUP_NAME}
                      value={values[ADD_CHAT_GROUP_FIELDS.GROUP_NAME]}
                      onChangeCb={(e) => {
                        setFieldValue(
                          ADD_CHAT_GROUP_FIELDS.GROUP_NAME,
                          e.target.value
                        );
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[ADD_CHAT_GROUP_FIELDS.GROUP_NAME] &&
                        Boolean(errors[ADD_CHAT_GROUP_FIELDS.GROUP_NAME])
                      }
                      helperText={
                        touched[ADD_CHAT_GROUP_FIELDS.GROUP_NAME]
                          ? errors[ADD_CHAT_GROUP_FIELDS.GROUP_NAME]
                          : undefined
                      }
                    />
                  </div>
                  <div className="pt-4">
                    <EventEHRMultiSelectWithSearch
                      top100Films={dropdownOptionsForUser}
                      setPageCount={setPageCountForUser}
                      setResetFlag={setResetFlagForUser}
                      handleSearch={(e: any) => {
                        handleSearchedValueChange(e.target.value);
                      }}
                      values={values[ADD_CHAT_GROUP_FIELDS.USER_NAME]}
                      handleChange={(data, selected) => {
                        setFieldValue(
                          ADD_CHAT_GROUP_FIELDS.USER_NAME,
                          selected
                        );
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[ADD_CHAT_GROUP_FIELDS.USER_NAME] &&
                        Boolean(errors[ADD_CHAT_GROUP_FIELDS.USER_NAME])
                      }
                      helperText={
                        touched[ADD_CHAT_GROUP_FIELDS.USER_NAME]
                          ? errors[ADD_CHAT_GROUP_FIELDS.USER_NAME]
                          : undefined
                      }
                    />
                  </div>
                </div>
              </DialogContent>

              {/* ACTION BUTTON */}
              <DialogActions style={{ padding: "24px" }}>
                {dialogHeading?.includes("Edit") && (
                  <Button
                    label={TEXT_CONSTANTS?.CANCEL}
                    variant="outlined"
                    onClickCb={handleClose}
                    style={{
                      width: "120px",
                      height: "44px",
                      padding: "12px 20px",
                      fontSize: "16px",
                      backgroundColor: "white",
                      border: "none",
                      color: "#393939",
                    }}
                  />
                )}
                <Button
                  type="submit"
                  label={"Save"}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    width: "200px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                  }}
                ></Button>
                {/* </div> */}
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
